import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface JobBoard {
  id: string;
  name: string;
  url: string;
  category: 'design_niche' | 'general';
  description?: string;
  logo_url?: string;
  country_scope?: string;
  remote_focus: boolean;
  has_rss: boolean;
  rss_url?: string;
  twitter_handle?: string;
  tags?: string[];
  job_type?: string[];
  niche?: string[];
  work_mode?: string[];
  geography?: string[];
  price_posting?: string;
  source: 'notion' | 'scraper';
  status: 'active' | 'dead_link' | 'unknown';
  created_at: string;
  updated_at: string;
  last_checked_at?: string;
}

export interface JobBoardFilters {
  categories: string[];
  countries: string[];
  remoteOnly: boolean | null;
  jobTypes: string[];
  niches: string[];
  workModes: string[];
  geographies: string[];
}

export const useJobBoards = (searchQuery?: string, filters?: JobBoardFilters, sortBy?: string) => {
  return useQuery({
    queryKey: ['jobBoards', searchQuery, filters, sortBy],
    queryFn: async () => {
      let query = supabase
        .from('job_boards')
        .select('*')
        .eq('status', 'active');

      // Apply filters
      if (filters?.categories?.length) {
        query = query.in('category', filters.categories);
      }

      if (filters?.countries?.length) {
        query = query.in('country_scope', filters.countries);
      }

      if (filters?.remoteOnly === true) {
        query = query.eq('remote_focus', true);
      } else if (filters?.remoteOnly === false) {
        query = query.eq('remote_focus', false);
      }

      if (filters?.jobTypes?.length) {
        query = query.overlaps('job_type', filters.jobTypes);
      }

      if (filters?.niches?.length) {
        query = query.overlaps('niche', filters.niches);
      }

      if (filters?.workModes?.length) {
        query = query.overlaps('work_mode', filters.workModes);
      }

      if (filters?.geographies?.length) {
        query = query.overlaps('geography', filters.geographies);
      }

      // Apply search
      if (searchQuery) {
        // Use full text search on name, description, and tags
        query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      // Apply sorting
      switch (sortBy) {
        case 'name':
          query = query.order('name', { ascending: true });
          break;
        case 'updated':
          query = query.order('created_at', { ascending: false });
          break;
        case 'relevance':
        default:
          // If searching, order by relevance (keep database order)
          // Otherwise, order by name
          if (!searchQuery) {
            query = query.order('name', { ascending: true });
          }
          break;
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return (data || []) as JobBoard[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get available filter options
export const useJobBoardFilters = () => {
  return useQuery({
    queryKey: ['jobBoardFilters'],
    queryFn: async () => {
      // Get all active job boards with their filter fields
      const { data: jobBoards } = await supabase
        .from('job_boards')
        .select('category, country_scope, job_type, niche, work_mode, geography')
        .eq('status', 'active');

      if (!jobBoards) return {
        categories: [],
        countries: [],
        jobTypes: [],
        niches: [],
        workModes: [],
        geographies: [],
      };

      const uniqueCategories = [...new Set(jobBoards.map(item => item.category))];
      const uniqueCountries = [...new Set(jobBoards.map(item => item.country_scope))].filter(Boolean);
      
      // Extract unique values from arrays
      const allJobTypes = jobBoards.flatMap(item => item.job_type || []);
      const allNiches = jobBoards.flatMap(item => item.niche || []);
      const allWorkModes = jobBoards.flatMap(item => item.work_mode || []);
      const allGeographies = jobBoards.flatMap(item => item.geography || []);

      const uniqueJobTypes = [...new Set(allJobTypes)].sort();
      const uniqueNiches = [...new Set(allNiches)].sort();
      const uniqueWorkModes = [...new Set(allWorkModes)].sort();
      const uniqueGeographies = [...new Set(allGeographies)].sort();

      return {
        categories: uniqueCategories,
        countries: uniqueCountries.sort(),
        jobTypes: uniqueJobTypes,
        niches: uniqueNiches,
        workModes: uniqueWorkModes,
        geographies: uniqueGeographies,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
